import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client/react';
import cn from 'clsx';
import style from './style.module.scss';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { TextAreaController } from '@components-clean/core/text-area';
import { ExtendedRiskObservationFields } from '@pages-clean/risk-assessment/risk-log/constants';
import { useTranslation } from '@hooks/index';
import { ShowError } from '@utils/notification';
import { DELETE_RISK_OBSERVATION, GET_RISK_OBSERVATIONS_AND_ACTIONS } from '@queries/risk-assessment';

export interface ObservationDeleteDialogProps {
    onClose: () => void;
    onSubmit: () => void;
    observation: ExtendedRiskObservationFields;
    riskId: string;
    studyId: string;
}

export type FormInputs = {
    deleteReason: string | null;
};

const DeleteObservationDialog = ({ onClose, onSubmit, observation, riskId, studyId }: ObservationDeleteDialogProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs.deleteObservation' });
    const { t: tCommon } = useTranslation('common');
    const [deleteRiskObsMutation] = useMutation(DELETE_RISK_OBSERVATION);
    const numActions = observation.numActions ?? 0;
    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<FormInputs>({
        mode: 'all',
        defaultValues: {
            deleteReason: null,
        },
    });

    const onDelete = async (data: FormInputs) => {
        deleteRiskObsMutation({
            variables: {
                id: observation.id,
                reason: data.deleteReason,
            },
            refetchQueries: [
                {
                    query: GET_RISK_OBSERVATIONS_AND_ACTIONS,
                    variables: {
                        studyId,
                        riskId,
                    },
                },
            ],
        })
            .then((response) => {
                if (!response?.data || !response?.data?.deleteRiskObservation) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                onSubmit();
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const warningText = (
        <span className={cn('k-text-left', style.fontSize)}>
            {t('warningMessage', {
                count: numActions,
            })}
        </span>
    );
    return (
        <DraggableDialog
            dataTestId="delete-observation-warning"
            onSubmit={() => handleSubmit(onDelete)()}
            onClose={onClose}
            title={`${t('title')}: ${observation.recordId}`}
            initialWidth={530}
            initialHeight={280}
            height={null}
            primaryBtnLabel={t('common:delete')}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
        >
            <div className="k-h-full k-w-full k-d-flex k-flex-column k-gap-2">
                {numActions > 0 && <InfoField type="Warning" message={warningText} />}

                <TextAreaController
                    className={style.textAreaOverrides}
                    maxLength={3000}
                    control={control}
                    name="deleteReason"
                    required
                    data-testid="field-observation-deleteReason"
                    label={`${t('deleteReasonLabel')} *`}
                />
            </div>
        </DraggableDialog>
    );
};

export default DeleteObservationDialog;
